import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { ELocalStorage } from '../enums/ELocalStorage';
import { ThemeMode } from '../enums/types';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);

  currentTheme = signal<ThemeMode>('light');

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const stored = localStorage.getItem(ELocalStorage.THEME) as ThemeMode | null;
    if (stored) {
      this.setTheme(stored);
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.setTheme(prefersDark ? 'dark' : 'light');
    }

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem(ELocalStorage.THEME)) {
        this.setTheme(e.matches ? 'dark' : 'light');
      }
    });
  }

  toggleTheme(): void {
    const newTheme: ThemeMode = this.currentTheme() === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(ELocalStorage.THEME, newTheme);
    }
  }

  setTheme(theme: ThemeMode): void {
    this.currentTheme.set(theme);
    if (isPlatformBrowser(this.platformId)) {
      const htmlEl = this.document.documentElement;
      if (theme === 'dark') {
        htmlEl.classList.add('dark');
      } else {
        htmlEl.classList.remove('dark');
      }
    }
  }

  isDark(): boolean {
    return this.currentTheme() === 'dark';
  }
}
